import ComponentManager from "../../../componentFramework";
import PrimeFacesUtil from "../PrimeFacesUtil";
import EventListenerManager from "../../EventListenerManager";
import EventManager from "../../EventManager";
import Log from "../../../scripts/Log";
import Messages from "../../Messages";

// PrimeFaces widget documentation: https://primefaces.github.io/primefaces/jsdocs/classes/primefaces.widget.fileupload.html
// PrimeFaces uses the following JQuery plugin which we can get by calling getJQ().
//		https://github.com/blueimp/jQuery-File-Upload/wiki/API

export interface PrimeFacesFileUploadEventMap {
	"fileListChange": {files: Array<File>};
	"uploadComplete": {};
	"uploadQueueComplete": {};
}

/**
 */
@ComponentManager.register()
export default class PrimeFacesFileUpload {
	public static componentStyleClass = "ui-fileupload";

	private static readonly activeDragStyleClass = "js-primeFacesFileUpload-isDragOver";

	private readonly eventListenerManager = new EventListenerManager();

	private mutationObserver: MutationObserver | undefined;
	private flatFilesListAtLastCheck: string | undefined;

	private readonly eventManager = new EventManager<PrimeFacesFileUploadEventMap>();
	public readonly addEventListener = this.eventManager.addEventListener;
	public readonly removeEventListener = this.eventManager.removeEventListener;

	constructor(private readonly rootElement: HTMLElement) {
	}

	@ComponentManager.on("ready")
	private componentReady() {
		PrimeFacesUtil.addWidgetListener("init", this.getPrimeFacesWidgetId(), this.onWidgetReady, true);
	}

	private onWidgetReady = (widget: any) => {
		if (!this.rootElement.isConnected) {
			return;
		}

		// Add visual feedback for drop zone
		const rootElementManaged = this.eventListenerManager.manage(this.rootElement);

		rootElementManaged.addEventListener("dragover", event => {
			if (PrimeFacesFileUpload.doesDragContainFiles(event)) {
				this.rootElement.classList.add(PrimeFacesFileUpload.activeDragStyleClass);
			}
		});

		const onDragDone = () => {
			this.rootElement.classList.remove(PrimeFacesFileUpload.activeDragStyleClass);
		}
		rootElementManaged.addEventListener("dragleave", onDragDone);
		rootElementManaged.addEventListener("drop", onDragDone);

		/* TODO (Michael)
		widget.cfg.onstart = Update status
		widget.cfg.onerror = Update status; Display
		 */
		// Put oncomplete in place
		const _this = this;
		const originalOnComplete = widget.cfg.oncomplete;
		widget.cfg.oncomplete = function(...args: any[]) {
			_this.eventManager.notifyEventListeners("uploadComplete", {});
			if (_this.getFiles().length === 0) {
				_this.eventManager.notifyEventListeners("uploadQueueComplete", {});
			}
			if (originalOnComplete !== undefined && originalOnComplete !== null) {
				return originalOnComplete.apply(this, args);
			}
		};

		// Put onerror in place
		const originalOnError = widget.cfg.onerror;
		widget.cfg.onerror = function(...args: any[]) {
			Log.error("File upload error:", args);
			Messages.createDesktopMessages(["Error uploading files. Please try again."], "error");

			if (originalOnError !== undefined && originalOnError !== null) {
				return originalOnError.apply(this, args);
			}
		};

		// Watch for changes to files list
		// While it is possible to watch for events that say when a file is added, they don't have a way to monitor for removals
		// So we are going to instead watch for mutations and check the files list
		this.mutationObserver = new MutationObserver(this.onMutation);
		this.mutationObserver.observe(this.rootElement, {
			childList: true,
			subtree: true,
			characterData: false,
			attributes: false
		});
		this.onMutation(); // run once
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventManager.destroy();
		this.mutationObserver?.disconnect();
		this.eventListenerManager.removeListeners();
	}

	public upload() {
		// Note while there is a method I can call, they mark it private in their documentation. That method is: this.getPrimeFacesWidget().upload();
		//		A click on the button does do a little more than call that upload() so I am going the safer route here.
		this.getPrimeFacesWidget().uploadButton.click();
	}

	private onMutation = () => {
		const files = this.getFiles();
		const flatFilesList = files.reduce((a, b) => a + b.name, "");

		if (flatFilesList !== this.flatFilesListAtLastCheck) {
			this.flatFilesListAtLastCheck = flatFilesList;
			this.eventManager.notifyEventListeners("fileListChange", {files});
		}
	}

	public getFiles(): Array<File> {
		return this.getPrimeFacesWidget().files as Array<File>;
	}

	public setUploadButtonVisibility(visible: boolean) {
		this.getPrimeFacesWidget()?.uploadButton?.get(0).classList.toggle("ui-button--hidden", !visible);
	}

	private getPrimeFacesWidget(): any|null {
		return PrimeFacesUtil.getWidgetById(this.getPrimeFacesWidgetId());
	}

	private getPrimeFacesWidgetId() {
		return this.rootElement.id;
	}

	private static doesDragContainFiles(event: DragEvent) {
		return event.dataTransfer !== null && event.dataTransfer.types.indexOf("Files") !== -1;
	}
}
